import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../context/AuthContext';
import { customPushService } from '../../services/customPushService';
import { ICustomPushNotificationLog, ICustomPushNotificationLogFilterData } from '../../types/customPush';

const formatMessage = (message?: string): string => {
  if (!message) return '';
  try {
    return JSON.stringify(JSON.parse(message), null, 2);
  } catch {
    return message;
  }
};

const PAGE_SIZE = 25;

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'sent':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
      return 'info';
    case 'failed':
    case 'invalid_api_key':
    case 'bad_request':
    case 'forbidden_notification_type':
      return 'error';
    default:
      return 'default';
  }
};

const LogsTab: React.FC = () => {
  const { hasRole } = useAuth();
  const canViewRequest = hasRole(['custom-push:admin']);

  const [logs, setLogs] = useState<ICustomPushNotificationLog[]>([]);
  const [filterData, setFilterData] = useState<ICustomPushNotificationLogFilterData>({
    apiKeys: [],
    notificationTypes: [],
  });
  const [apiKeyId, setApiKeyId] = useState('');
  const [accountRef, setAccountRef] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<ICustomPushNotificationLog | null>(null);

  const loadFilterData = useCallback(async () => {
    try {
      const data = await customPushService.getLogFilterData();
      setFilterData(data);
    } catch (error) {
      console.error('Error loading log filter data:', error);
    }
  }, []);

  const loadLogs = useCallback(async (offsetOverride?: number) => {
    setLoading(true);
    try {
      const data = await customPushService.getLogs({
        apiKeyId: apiKeyId || undefined,
        accountRef: accountRef || undefined,
        notificationType: notificationType || undefined,
        limit: PAGE_SIZE,
        offset: offsetOverride ?? offset,
      });
      setLogs(data);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeyId, accountRef, notificationType, offset]);

  useEffect(() => {
    loadFilterData();
    loadLogs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setOffset(0);
    loadLogs(0);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - PAGE_SIZE);
    setOffset(newOffset);
    loadLogs(newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    loadLogs(newOffset);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
        Notification Logs
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <FormControl size="small" sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <InputLabel id="log-api-key-label">API Key</InputLabel>
            <Select
              labelId="log-api-key-label"
              label="API Key"
              value={apiKeyId}
              onChange={(e) => setApiKeyId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {filterData.apiKeys.map((key) => (
                <MenuItem key={key.apiKeyId} value={key.apiKeyId}>
                  {key.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <InputLabel id="log-notification-type-label">Notification Type</InputLabel>
            <Select
              labelId="log-notification-type-label"
              label="Notification Type"
              value={notificationType}
              onChange={(e) => setNotificationType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {filterData.notificationTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Account Ref"
            value={accountRef}
            onChange={(e) => setAccountRef(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />

          <Button variant="contained" onClick={handleSearch} sx={{ minWidth: '120px', height: '40px' }}>
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Requested On</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>API Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notification Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Client Ids</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sent / Failed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                  {canViewRequest && <TableCell sx={{ fontWeight: 600 }}>Request</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((entry) => (
                  <TableRow key={entry.logId} hover>
                    <TableCell>{entry.requestedOn ? new Date(entry.requestedOn).toLocaleString() : ''}</TableCell>
                    <TableCell>{entry.apiKeyName || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.notificationType || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                      {entry.clientIds?.join(', ') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={entry.status} color={statusColor(entry.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      {entry.successCount ?? '—'} / {entry.failureCount ?? '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {entry.errorMessage ? (
                        <Tooltip title={entry.errorMessage}>
                          <Typography variant="body2" noWrap>{entry.errorMessage}</Typography>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    {canViewRequest && (
                      <TableCell>
                        {entry.message ? (
                          <Tooltip title="View request">
                            <IconButton size="small" onClick={() => setViewingRequest(entry)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canViewRequest ? 8 : 7}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No log entries found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} p={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Button size="small" startIcon={<ChevronLeftIcon />} disabled={offset === 0} onClick={handlePrev}>
              Previous
            </Button>
            <Button size="small" endIcon={<ChevronRightIcon />} disabled={logs.length < PAGE_SIZE} onClick={handleNext}>
              Next
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog open={!!viewingRequest} onClose={() => setViewingRequest(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Payload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {viewingRequest?.apiKeyName} · {viewingRequest?.notificationType}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              overflow: 'auto',
              fontSize: '0.8125rem',
              fontFamily: 'monospace',
            }}
          >
            {formatMessage(viewingRequest?.message)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewingRequest(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogsTab;
